/**
 * SSOT Registry — data-driven catalog of every single-source-of-truth.
 *
 * GOVERNANCE_DETANGLE_PLAN_2026 §17 (SSOT consolidation + admin insight).
 *
 * The codebase has many SSOTs (tier table, DOK floors/ceilings, module
 * cognitive profiles, per-type cognitive bounds, question-type ceilings,
 * visual roles, …). They were discoverable only by reading source. This
 * registry turns "which SSOTs exist + what do they currently say" into
 * DATA: each entry carries metadata PLUS a `read()` that returns the LIVE
 * values from the actual SSOT at call time, so the catalog can never drift
 * from code and can be rendered for insight in the admin portal.
 */

/** Broad domain a SSOT belongs to — used for grouping in the admin. */
export type SsotDomain =
  | 'taxonomy'
  | 'cognitive'
  | 'governance'
  | 'pipeline'
  | 'content'
  | 'visuals'
  | 'design'
  | 'mastery'

/** A single column in a SSOT's live snapshot table. */
export interface SsotColumn {
  key: string
  label: string
}

/**
 * A live, render-ready view of a SSOT's current values. Computed on demand
 * by {@link SsotEntry.read} so it always reflects the running code.
 */
export interface SsotSnapshot {
  columns: SsotColumn[]
  rows: Array<Record<string, string | number>>
}

/** One catalogued single-source-of-truth. */
export interface SsotEntry {
  /** Stable identifier (kebab-case). */
  id: string
  /** Human-readable name shown in the admin. */
  name: string
  /** Domain grouping. */
  domain: SsotDomain
  /** Source file path (for "go to source" affordance). */
  file: string
  /** Canonical export(s) that constitute the SSOT. */
  exportName: string
  /** One-line description of what this SSOT is. */
  description: string
  /** What decision / behaviour this SSOT drives. */
  governs: string
  /** Known read-sites / consumers. */
  consumers: string[]
  /** Optional drift / caveat note (e.g. shadow-gated reconciliation). */
  notes?: string
  /** Returns the LIVE values of this SSOT, computed at call time. */
  read: () => SsotSnapshot
}
